/** Inline-Bearbeitung einer eigenen Nachricht (Enter speichert, Esc bricht ab, leer → löschen). */
import type { MessageDto } from '@lagerfeuer/shared';
import { isDm } from '@lagerfeuer/shared';
import { useEffect, useRef, useState } from 'react';
import { useAppStore } from '../../store/app';
import { dmName } from '../../store/selectors';
import { editMessage, useMessageDialog } from './actions';
import { toDisplay, toRaw, type TokenMap } from './composerText';
import { allServerEmojis } from './emojiCache';
import { focusComposer, useMessagesUi } from './state';

export function MessageEditor({ message }: { message: MessageDto }) {
  const serverId = message.serverId;
  const [init] = useState(() => {
    const s = useAppStore.getState();
    const map: TokenMap = {};
    const display = toDisplay(
      message.content,
      {
        user: (id) => {
          const m = serverId ? s.members[serverId]?.[id] : undefined;
          const u = m?.user ?? s.users[id];
          return u ? { name: m?.nickname || u.displayName || u.username, username: u.username } : null;
        },
        role: (id) => (serverId ? (s.roles[serverId]?.find((r) => r.id === id)?.name ?? null) : null),
        channel: (id) => {
          const c = s.channels[id];
          return c ? (isDm(c.type) ? dmName(c, s.me?.id) : c.name) : null;
        },
      },
      map,
    );
    return { map, display };
  });
  const [text, setText] = useState(init.display);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  const maxLen = useAppStore((s) => s.limits.maxMessageLength);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 400)}px`;
  }, [text]);

  const cancel = () => {
    useMessagesUi.getState().setEditing(message.channelId, null);
    focusComposer(message.channelId);
  };
  const rawOf = (value: string) => {
    const emojis = allServerEmojis(useAppStore.getState());
    return toRaw(value, init.map, (name) => emojis.find((e) => e.name === name && (e.serverId === serverId || !serverId)) ?? emojis.find((e) => e.name === name));
  };
  const save = async () => {
    const raw = rawOf(text.trim());
    if (!raw && !message.attachments.length && !message.stickers.length) {
      useMessageDialog.getState().open({ kind: 'delete', message });
      cancel();
      return;
    }
    if (raw === message.content) {
      cancel();
      return;
    }
    if (raw.length > maxLen) return;
    setBusy(true);
    const ok = await editMessage(message, raw);
    setBusy(false);
    if (ok) cancel();
  };

  // Limit gilt für den gesendeten Rohtext (Emoji-/Erwähnungs-Token werden länger).
  const rawLength = rawOf(text.trim()).length;
  const over = rawLength > maxLen;
  return (
    <div className="msg-editor" data-testid="message-editor">
      <textarea
        ref={ref}
        className="msg-editor-input"
        value={text}
        rows={1}
        aria-label="Nachricht bearbeiten"
        aria-invalid={over}
        disabled={busy}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            cancel();
          } else if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            void save();
          }
        }}
        data-testid="message-editor-input"
      />
      <div className="msg-editor-hint small">
        Esc zum{' '}
        <button type="button" className="btn-link" onClick={cancel} data-testid="message-editor-cancel">
          Abbrechen
        </button>{' '}
        • Enter zum{' '}
        <button type="button" className="btn-link" onClick={() => void save()} disabled={over || busy} data-testid="message-editor-save">
          Speichern
        </button>
        {over && <span className="error-text"> · {rawLength - maxLen} Zeichen zu viel</span>}
      </div>
    </div>
  );
}
